import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play, ChevronRight, ShieldCheck, Layers, Workflow, Bot, BookOpen, PackageCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, PersonaStamp } from "@/components/app/primitives";
import {
  WORKING_ROOM, SIX_FIT_ZONES, OPERATING_STAGES, AGENT_LAYER,
  FOUNDATIONS, SYSTEM_OUTPUTS, MODULE_MANUAL,
} from "@/lib/overviewConfig";

const TONE_TEXT = { neutral: "text-[--tx-1]", ai: "text-[--ai]", risk: "text-[--amber]", validated: "text-[--green]", crimson: "text-[#E86A6A]" };
const TONE_BORDER = { neutral: "border-t-[--tx-3]", ai: "border-t-[--ai]", risk: "border-t-[--amber]", validated: "border-t-[--green]", crimson: "border-t-[--six-crimson]" };

const SectionHeader = ({ icon: Icon, kicker, title, sub, testId }) => (
  <div className="mb-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-[#E86A6A] mb-1">
      <Icon size={13} /> {kicker}
    </div>
    <h2 className="font-display text-lg font-semibold text-[--tx-1]">{title}</h2>
    {sub && <p className="text-[13px] text-[--tx-2] mt-1 max-w-4xl leading-relaxed">{sub}</p>}
  </div>
);

const ModuleLink = ({ to, label, testId }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-md border border-[--hairline] bg-[--shell-2] px-2 py-1 text-[11px] font-medium text-[--tx-2] hover:text-[--tx-1] hover:border-[--ai] active:scale-[0.98] transition-colors"
    >
      {label} <ArrowUpRight size={10} className="opacity-60" />
    </button>
  );
};

const fade = (i = 0) => ({ initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.05, duration: 0.2 } });

export default function TransformationOSPage() {
  const navigate = useNavigate();
  const { startSimulation, overview } = useApp();
  const state = overview?.state;

  return (
    <div data-testid="overview-page" className="space-y-10">

      {/* ============ 1. WORKING-ROOM HEADER ============ */}
      <section data-testid="overview-header-section">
        <PageHeader
          kicker={WORKING_ROOM.kicker}
          title={WORKING_ROOM.title}
          subtitle={WORKING_ROOM.subtitle}
          testId="overview-page-header"
          right={
            <div className="flex items-center gap-2">
              <button onClick={startSimulation} data-testid="overview-start-simulation-button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
                <Play size={13} /> Start executive walkthrough
              </button>
              <button onClick={() => navigate("/briefing")} data-testid="overview-open-briefing-button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3.5 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] hover:bg-[#243041] active:scale-[0.98] transition-colors">
                Consortium Briefing <ArrowRight size={13} />
              </button>
            </div>
          }
        />
        <div className="grid md:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="md:col-span-3 xl:col-span-2 rounded-xl border border-[--six-crimson] bg-[--six-crimson-soft] p-4" data-testid="overview-mission-tile">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#E86A6A] mb-1">{WORKING_ROOM.mission.label}</div>
            <div className="font-display text-xl md:text-2xl font-semibold text-[--tx-1] tabular-nums">{WORKING_ROOM.mission.value}</div>
            <div className="text-xs text-[--tx-2] mt-1 leading-snug">{WORKING_ROOM.mission.note}</div>
          </div>
          {WORKING_ROOM.stats.map((s, i) => (
            <motion.div key={s.label} {...fade(i)} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-4" data-testid={`overview-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`font-display text-2xl md:text-3xl font-semibold tabular-nums ${TONE_TEXT[s.tone]}`}>{s.value}</div>
              <div className="text-[11px] uppercase tracking-wider text-[--tx-3] font-medium mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
        {state?.phase && (
          <div className="flex items-center gap-2 mt-3">
            <StatusChip tone="crimson" data-testid="overview-phase-chip">{state.phase}</StatusChip>
            <StatusChip tone="ai" data-testid="overview-confidence-chip">{state.overall_confidence}</StatusChip>
          </div>
        )}
      </section>

      {/* ============ 2. WHERE SIX FITS ============ */}
      <section data-testid="overview-six-fit-section">
        <SectionHeader
          icon={Layers}
          kicker="Section 01"
          title="Where SIX fits"
          sub="SIX operates in three zones of your organization - each one sits between two things you already own and closes the distance between them."
          testId="overview-six-fit-header"
        />
        <div className="grid lg:grid-cols-3 gap-4">
          {SIX_FIT_ZONES.map((z, i) => (
            <motion.div key={z.id} {...fade(i)}>
              <div className={`h-full rounded-xl border border-[--hairline] border-t-2 ${TONE_BORDER[z.tone]} bg-[--panel-dark] p-4`} data-testid={`overview-zone-${z.id}`}>
                <StatusChip tone={z.tone} className="mb-2.5">{z.role}</StatusChip>
                <h3 className="font-display text-sm font-semibold text-[--tx-1] mb-2">{z.title}</h3>
                <p className="text-[13px] text-[--tx-2] leading-relaxed mb-3">{z.body}</p>
                <div className="flex flex-wrap gap-1.5">
                  {z.links.map((l) => <ModuleLink key={l.to} to={l.to} label={l.label} testId={`overview-zone-link-${z.id}-${l.to.slice(1)}`} />)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 3. THE OPERATING ARCHITECTURE ============ */}
      <section data-testid="overview-architecture-section">
        <SectionHeader
          icon={Workflow}
          kicker="Section 02"
          title="The operating architecture"
          sub="Five stages, left to right. Every workspace in the left panel belongs to exactly one stage - click any module chip to enter it."
          testId="overview-architecture-header"
        />
        <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="overview-stage-flow">
          {OPERATING_STAGES.map((st, i) => (
            <motion.div key={st.id} {...fade(i)} className="relative">
              <div className="h-full rounded-xl border border-[--hairline] bg-[--panel-dark] p-4 flex flex-col" data-testid={`overview-stage-${st.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-display text-lg font-bold tabular-nums text-[#E86A6A]">{st.num}</span>
                  <h3 className="font-display text-sm font-semibold text-[--tx-1]">{st.name}</h3>
                  {i < OPERATING_STAGES.length - 1 && (
                    <ChevronRight size={14} className="ml-auto text-[--tx-3] hidden xl:block" />
                  )}
                </div>
                <p className="text-xs text-[--tx-2] leading-relaxed mb-3 flex-1">{st.purpose}</p>
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-start gap-1.5 text-[11px]">
                    <span className="font-mono uppercase tracking-wider text-[--tx-3] shrink-0 w-8">In</span>
                    <span className="text-[--tx-2]">{st.input}</span>
                  </div>
                  <div className="flex items-start gap-1.5 text-[11px]">
                    <span className="font-mono uppercase tracking-wider text-[--tx-3] shrink-0 w-8">Out</span>
                    <span className="text-[--green]">{st.output}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-2.5 border-t border-[--divider]">
                  {st.modules.map((m) => <ModuleLink key={m.to} to={m.to} label={m.label} testId={`overview-stage-link-${st.id}-${m.to.slice(1)}`} />)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 4. THE AGENTIC DECISION LAYER ============ */}
      <section data-testid="overview-agent-layer-section">
        <SectionHeader
          icon={Bot}
          kicker="Section 03"
          title="The agentic decision layer"
          sub={AGENT_LAYER.intro}
          testId="overview-agent-layer-header"
        />
        <div className="grid lg:grid-cols-5 gap-3 mb-4">
          {AGENT_LAYER.groups.map((g, i) => (
            <motion.div key={g.id} {...fade(i)}>
              <div className={`h-full rounded-xl border border-[--hairline] border-t-2 ${TONE_BORDER[g.tone]} bg-[--panel-dark] p-3.5`} data-testid={`overview-agent-group-${g.id}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">{g.name}</div>
                <div className="space-y-2.5">
                  {g.agents.map((a) => (
                    <div key={a.id} data-testid={`overview-agent-${a.id.toLowerCase()}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] text-[--tx-3]">{a.id}</span>
                        <span className={`text-xs font-semibold ${TONE_TEXT[g.tone]}`}>{a.name}</span>
                      </div>
                      <div className="text-[11px] text-[--tx-3] leading-snug mt-0.5">{a.covers}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <Panel title="How a finding travels" className="lg:col-span-2" testId="overview-routing-panel">
            <div className="space-y-0">
              {AGENT_LAYER.routing.map((r, i) => (
                <div key={r.step} className="flex items-start gap-3 relative" data-testid={`overview-routing-step-${i}`}>
                  <div className="flex flex-col items-center shrink-0">
                    <span className={`w-2 h-2 rounded-full mt-1.5 ${{ neutral: "bg-[--tx-3]", ai: "bg-[--ai]", risk: "bg-[--amber]", validated: "bg-[--green]", crimson: "bg-[--six-crimson]" }[r.tone]}`} />
                    {i < AGENT_LAYER.routing.length - 1 && <span className="w-px flex-1 min-h-[22px] bg-[--divider]" />}
                  </div>
                  <div className="pb-3.5">
                    <div className={`text-xs font-semibold ${TONE_TEXT[r.tone === "neutral" ? "neutral" : r.tone]}`}>{r.step}</div>
                    <div className="text-[12px] text-[--tx-2] leading-relaxed">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="The three senior lenses" testId="overview-personas-panel">
            <div className="space-y-3">
              {AGENT_LAYER.personas.map((p) => (
                <div key={p.code} className="flex items-center gap-3" data-testid={`overview-persona-${p.code}`}>
                  <PersonaStamp code={p.code} />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[--tx-1]">{p.name}</div>
                    <div className="text-[11px] text-[--tx-3] leading-snug">{p.lens}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/triad")} data-testid="overview-open-triad-button"
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] hover:bg-[#243041] active:scale-[0.98] transition-colors">
              Enter the Triad Review Room <ArrowRight size={12} />
            </button>
          </Panel>
        </div>
      </section>

      {/* ============ 5. FOUNDATIONS AND GUARDRAILS ============ */}
      <section data-testid="overview-foundations-section">
        <SectionHeader
          icon={ShieldCheck}
          kicker="Section 04"
          title="Foundations and guardrails"
          sub="The rules the system enforces on itself. These are not preferences - they are how the environment prevents overclaiming, silent failure, and unowned findings."
          testId="overview-foundations-header"
        />
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
          {FOUNDATIONS.map((f, i) => (
            <motion.div key={f.id} {...fade(i % 4)}>
              <div className="h-full rounded-xl border border-[--hairline] bg-[--panel-dark] p-4" data-testid={`overview-foundation-${f.id}`}>
                <StatusChip tone={f.tone} className="mb-2">{f.name}</StatusChip>
                <p className="text-[12px] text-[--tx-2] leading-relaxed">{f.rule}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ 6. WHAT THE SYSTEM PRODUCES ============ */}
      <section data-testid="overview-outputs-section">
        <SectionHeader
          icon={PackageCheck}
          kicker="Section 05"
          title="What the system produces"
          sub="Ten governed artifacts. Each one lives in a workspace, stays current as the engagement moves, and can be opened directly from here."
          testId="overview-outputs-header"
        />
        <div className="grid md:grid-cols-2 gap-2.5">
          {SYSTEM_OUTPUTS.map((o, i) => (
            <motion.button
              key={o.id}
              {...fade(i % 5)}
              onClick={() => navigate(o.to)}
              data-testid={`overview-output-${o.id}`}
              className="group flex items-start gap-3 rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5 text-left hover:border-[--ai] active:scale-[0.99] transition-colors"
            >
              <span className="font-mono text-[10px] text-[--tx-3] pt-0.5 tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[--tx-1]">{o.name}</span>
                <span className="block text-[12px] text-[--tx-2] leading-relaxed mt-0.5">{o.detail}</span>
              </span>
              <ArrowUpRight size={13} className="shrink-0 mt-0.5 text-[--tx-3] group-hover:text-[--ai] transition-colors" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* ============ 7. MODULE DIRECTORY - USER MANUAL ============ */}
      <section data-testid="overview-manual-section">
        <SectionHeader
          icon={BookOpen}
          kicker="Section 06"
          title="Module directory"
          sub="The operating manual for every workspace in the left panel - what it is, what runs underneath it, what you do there, and what it produces. Grouped exactly as the navigation."
          testId="overview-manual-header"
        />
        <div className="space-y-6">
          {MODULE_MANUAL.map((grp) => (
            <div key={grp.group} data-testid={`overview-manual-group-${grp.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-baseline gap-3 mb-2.5">
                <h3 className="font-display text-sm font-semibold text-[--tx-1]">{grp.group}</h3>
                <span className="text-[12px] text-[--tx-3]">{grp.blurb}</span>
              </div>
              <div className="grid lg:grid-cols-2 gap-3">
                {grp.modules.map((m) => (
                  <div key={m.to} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-4" data-testid={`overview-manual-module-${m.to.slice(1)}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="font-display text-[13px] font-semibold text-[--tx-1] truncate">{m.name}</h4>
                        <span className="font-mono text-[10px] text-[--tx-3] shrink-0">{m.to}</span>
                      </div>
                      <button onClick={() => navigate(m.to)} data-testid={`overview-manual-open-${m.to.slice(1)}`}
                        className="inline-flex items-center gap-1 rounded-md border border-[--hairline] bg-[--shell-3] px-2 py-1 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] hover:bg-[#243041] active:scale-[0.98] transition-colors shrink-0">
                        Open <ArrowUpRight size={10} />
                      </button>
                    </div>
                    <p className="text-[12px] text-[--tx-2] leading-relaxed mb-3">{m.purpose}</p>
                    <div className="space-y-1.5 text-[11px]">
                      <div className="flex gap-2">
                        <span className="font-mono uppercase tracking-wider text-[--ai] shrink-0 w-[76px]">Runs on</span>
                        <span className="text-[--tx-2]">{m.runs_on}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-mono uppercase tracking-wider text-[--amber] shrink-0 w-[76px]">Use it to</span>
                        <span className="text-[--tx-2]">{m.use_it_to}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-mono uppercase tracking-wider text-[--green] shrink-0 w-[76px]">Produces</span>
                        <span className="text-[--tx-2]">{m.produces}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing rail */}
      <div className="rounded-xl border border-[--divider] bg-[--shell-2] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="overview-closing-rail">
        <p className="text-xs text-[--tx-3] leading-relaxed max-w-3xl">
          The environment is a loop, not a line: benefit tracking feeds new hypotheses, every cycle raises data confidence, and SIX's role shrinks by design as Brightspeed owners take the system over.
        </p>
        <button onClick={startSimulation} data-testid="overview-footer-simulation-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
          <Play size={13} /> Walk the journey
        </button>
      </div>
    </div>
  );
}
